import type { ParagraphEntry, ParagraphIndex } from './indexParagraphs';
import { getIsEmptyCastParagraph } from '../helpers/getIsEmptyCastParagraph';

/**
 * Returns the immediate parent list items for a matched list paragraph, so
 * `1.1.1.` carries `1.1` and `1` along for context. Walks backwards in
 * document order, scoped to the same section, stopping when leaving the list
 * entirely. Blank paragraphs are spacing, not the end of the list — the walk
 * steps over them.
 */
export function expandParentListItems(
  entry: ParagraphEntry,
  index: ParagraphIndex
): ParagraphEntry[] {
  const matched = entry.paragraph;
  if (matched.listLevel === undefined || matched.listLevel <= 1) return [];

  if (matched.listType === 'numbered') {
    return findNumberedParents(entry, index);
  }
  if (matched.listType === 'bullet') {
    return findBulletParents(entry, index);
  }
  return [];
}

function findNumberedParents(entry: ParagraphEntry, index: ParagraphIndex): ParagraphEntry[] {
  const matched = entry.paragraph;
  if (matched.listMarker === undefined) return [];
  const parentMarkers = new Set(getParentMarkers(matched.listMarker));
  if (parentMarkers.size === 0) return [];

  const parents: ParagraphEntry[] = [];
  for (let i = entry.documentOrder - 1; i >= 0 && parentMarkers.size > 0; i--) {
    const candidate = index.ordered[i];
    if (candidate.section !== entry.section) break;
    const p = candidate.paragraph;
    if (getIsEmptyCastParagraph(p)) continue;
    if (p.listLevel === undefined && !candidate.ancestors.includes(entry.section)) break;
    if (p.listLevel === undefined) break;
    if (p.listMarker && parentMarkers.has(p.listMarker)) {
      parents.push(candidate);
      parentMarkers.delete(p.listMarker);
    }
  }
  return parents;
}

function findBulletParents(entry: ParagraphEntry, index: ParagraphIndex): ParagraphEntry[] {
  const matched = entry.paragraph;
  if (matched.listLevel === undefined) return [];
  const levelsNeeded = new Set<number>();
  for (let level = matched.listLevel - 1; level >= 1; level--) levelsNeeded.add(level);

  const parents: ParagraphEntry[] = [];
  for (let i = entry.documentOrder - 1; i >= 0 && levelsNeeded.size > 0; i--) {
    const candidate = index.ordered[i];
    if (candidate.section !== entry.section) break;
    const p = candidate.paragraph;
    if (getIsEmptyCastParagraph(p)) continue;
    if (p.listLevel === undefined) break;
    if (p.listType === 'bullet' && levelsNeeded.has(p.listLevel)) {
      parents.push(candidate);
      levelsNeeded.delete(p.listLevel);
    }
  }
  return parents;
}

/**
 * From `"1.2.3."` returns `["1.2.", "1."]`. The trailing period that
 * extractors emit is preserved on each level so equality matches what
 * other list paragraphs carry as their own marker.
 */
function getParentMarkers(marker: string): string[] {
  const trimmed = marker.endsWith('.') ? marker.slice(0, -1) : marker;
  const parts = trimmed.split('.');
  if (parts.length <= 1) return [];
  const parents: string[] = [];
  for (let i = parts.length - 1; i > 0; i--) {
    parents.push(`${parts.slice(0, i).join('.')}.`);
  }
  return parents;
}
